import { Routes, Route, Navigate } from 'react-router-dom'
import { LoginPage } from './pages/login/page'
import { DashboardPage } from './pages/dashboard/page'
import { SelectProjectPage } from './pages/select-project/page'
import { ProtectedRoute } from './components/auth/protected-route.component'
import { ProjectLayout } from './components/layouts/project-layout.component'
import { MedicalLayout } from './pages/medical/_components/medical-layout.component'
import PatientFileLayout from './pages/patients/patient-file/patient-file-layout.component'
import { PatientIdPage } from './pages/patients/patient-file/page'
import { PatientFilePatientFollowupPage } from './pages/patients/patient-file/patient-followup/page'
import AddPatientFollowupPage from './pages/patients/patient-file/add-patient-followup/page'
import { ProjectsPage } from './pages/medical/projects/page'
import { LocaleWrapper } from './components/locale/locale-wrapper.component'
import PatientsPage from './pages/medical/patients/page'
import PlanningPage from './pages/medical/planning/page'
import { MedicalRedirectPage } from './pages/medical/page'
import ReviewsPage from './pages/medical/reviews/page'
import StatisticsPage from './pages/medical/statistics/page'
import AccountPage from './pages/medical/account/page'
import ConfigurationLayout from './pages/configuration/_components/configuration-layout.component'
import ConfigurationFollowupsPage from './pages/configuration/followups/page'
import ProgrammedMessagesPage from './pages/configuration/followups/[followupId]/messages/page'
import AddProgrammedMessagePage from './pages/configuration/followups/[followupId]/messages/add/page'
import ContentsPage from './pages/configuration/contents/page'
import AddContentPage from './pages/configuration/contents/add/page'
import AddContentContentPage from './pages/configuration/contents/add/content/page'
import AddExternalLinkContentPage from './pages/configuration/contents/add/external-link/page'
import UpdateContentPage from './pages/configuration/contents/update/page'
import ConfigurationFollowupIdPage from './pages/configuration/followups/[followupId]/page'
import AddChatbotPage from './pages/configuration/followups/[followupId]/chatbot/add/page'
import ChatbotGeneralFormEditPage from './pages/configuration/followups/[followupId]/chatbot/[chatbotId]/update-general-form/page'
import ChatbotUpdateNodePage from './pages/configuration/followups/[followupId]/chatbot/[chatbotId]/update-nodes/page'
import PermissionsPage from './pages/configuration/permissions/page'
import AddPermissionPage from './pages/configuration/permissions/add/page'
import UpdatePermissionPage from './pages/configuration/permissions/[permissionId]/page'
import MedicalStaffMembersPage from './pages/configuration/medical-staff-members/page'
import UserProjectListPage from './pages/medical/users/page'
import PatientAddPage from './pages/medical/patients/add/page'
import UpdateProgrammedMessagePage from './pages/configuration/followups/[followupId]/messages/[messageId]/page'
import ForgotPasswordPage from './pages/auth/forgot-password/page'
import PasswordFormPage from './pages/auth/password-form/page'
import { LifenConnectCallbackPage } from './pages/auth/lifen-connect-callback/page'
import AuthRouteLayout from './components/layouts/auth-route-layout.component'
import ExternalPatientPage from './pages/medical/patients/external/[remoteId]/page'
import UploadPage from './pages/medical/upload/page'
import ConfigurationFollowupsAddPage from './pages/configuration/followups/add/page'
import { FollowupIdGuard } from './pages/configuration/followups/[followupId]/_components/followup-id-guard.component'
import NotFoundPage from './pages/not-found/page'
import { NavigateToNotFound } from './pages/not-found/navigate-to-not-found.component'
import { HRLayout } from './pages/hr/_components/hr-layout.component'
import HrCampaignsPage from './pages/hr/campaigns/page'
import NewHrCampaignPage from './pages/hr/campaigns/new/page'
import AlertCampaignDetailPage from './pages/hr/campaigns/alert/[campaignId]/page'
import PlanBlancCampaignDetailPage from './pages/hr/campaigns/plan-blanc/[campaignId]/page'
import HrContactsPage from './pages/hr/contacts/page'

export const AppRoutes = () => {
  return (
    <Routes>
      {/* Root redirect to default locale */}
      <Route path="/" element={<Navigate to="/fr/auth/select-project" replace />} />

      {/* Locale wrapper - all routes are under /:locale */}
      <Route path="/:locale" element={<LocaleWrapper />}>
        {/* Redirect /:locale to /:locale/auth/select-project (mirrors Next.js middleware) */}
        <Route index element={<Navigate to="auth/select-project" replace />} />

        {/* Dedicated 404 page (standalone, no app chrome) */}
        <Route path="not-found" element={<NotFoundPage />} />

        {/* Auth route group with shared layout (white card + slogan) */}
        <Route element={<AuthRouteLayout />}>
          {/* Public routes */}
          <Route path="login" element={<LoginPage />} />
          {/* Legacy Next.js URL (old bookmarks/emails) — must not reach ProtectedRoute, which would store it as the post-login redirect */}
          <Route path="auth/login" element={<Navigate to="../login" replace />} />
          <Route path="auth/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="auth/password-form" element={<PasswordFormPage />} />
          <Route path="auth/lifen-connect/callback" element={<LifenConnectCallbackPage />} />

          {/* Protected route inside auth layout */}
          <Route element={<ProtectedRoute />}>
            <Route path="auth/select-project" element={<SelectProjectPage />} />
          </Route>
        </Route>

        {/* Protected routes outside auth layout */}
        <Route element={<ProtectedRoute />}>
          {/* Project routes with CurrentProjectProvider */}
          <Route path=":projectId" element={<ProjectLayout />}>
            {/* Redirect /:projectId to /:projectId/medical */}
            <Route index element={<Navigate to="medical" replace />} />

            {/* Medical area */}
            <Route path="medical" element={<MedicalLayout />}>
              {/* Redirect /:projectId/medical to dashboard or patients based on features */}
              <Route index element={<MedicalRedirectPage />} />
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="projects" element={<ProjectsPage />} />

              {/* Patient list */}
              <Route path="patients" element={<PatientsPage />} />
              <Route path="patients/add" element={<PatientAddPage />} />
              <Route path="patients/external/:remoteId" element={<ExternalPatientPage />} />

              {/* Planning */}
              <Route path="planning" element={<PlanningPage />} />

              {/* Reviews */}
              <Route path="reviews" element={<ReviewsPage />} />

              {/* Statistics */}
              <Route path="statistics" element={<StatisticsPage />} />

              {/* Account */}
              <Route path="account" element={<AccountPage />} />

              {/* Users */}
              <Route path="users" element={<UserProjectListPage />} />

              {/* Patient file routes */}
              <Route path="patients/:patientId" element={<PatientFileLayout />}>
                <Route index element={<PatientIdPage />} />
                <Route path="add-patient-followup" element={<AddPatientFollowupPage />} />
                <Route path=":patientFollowupId" element={<PatientFilePatientFollowupPage />} />
                <Route path="*" element={<NavigateToNotFound />} />
              </Route>

              {/* Upload */}
              <Route path="upload" element={<UploadPage />} />

              <Route path="*" element={<NavigateToNotFound />} />
            </Route>

            {/* HR area */}
            <Route path="hr" element={<HRLayout />}>
              <Route index element={<Navigate to="campaigns" replace />} />
              <Route path="campaigns" element={<HrCampaignsPage />} />
              <Route path="campaigns/new" element={<NewHrCampaignPage />} />
              <Route path="campaigns/alert/:campaignId" element={<AlertCampaignDetailPage />} />
              <Route path="campaigns/plan-blanc/:campaignId" element={<PlanBlancCampaignDetailPage />} />
              <Route path="contacts" element={<HrContactsPage />} />
              <Route path="*" element={<NavigateToNotFound />} />
            </Route>

            {/* Configuration area */}
            <Route path="configuration" element={<ConfigurationLayout />}>
              <Route index element={<Navigate to="followups" replace />} />
              <Route path="followups" element={<ConfigurationFollowupsPage />} />
              <Route path="followups/add" element={<ConfigurationFollowupsAddPage />} />
              <Route path="followups/:followupId" element={<FollowupIdGuard />}>
                <Route index element={<ConfigurationFollowupIdPage />} />
                <Route path="chatbot/add" element={<AddChatbotPage />} />
                <Route path="messages/add" element={<AddProgrammedMessagePage />} />
                <Route path="messages/:messageId" element={<UpdateProgrammedMessagePage />} />
                <Route
                  path="chatbot/:chatbotId/update-general-form"
                  element={<ChatbotGeneralFormEditPage />}
                />
                <Route path="chatbot/:chatbotId/update-nodes" element={<ChatbotUpdateNodePage />} />
              </Route>
              <Route path="messages" element={<ProgrammedMessagesPage />} />
              <Route path="contents" element={<ContentsPage />} />
              <Route path="contents/add" element={<AddContentPage />} />
              <Route path="contents/add/content" element={<AddContentContentPage />} />
              <Route path="contents/add/external-link" element={<AddExternalLinkContentPage />} />
              <Route path="contents/:contentId" element={<UpdateContentPage />} />
              <Route path="permissions" element={<PermissionsPage />} />
              <Route path="permissions/add" element={<AddPermissionPage />} />
              <Route path="permissions/:permissionId" element={<UpdatePermissionPage />} />
              <Route path="medical-staff-members" element={<MedicalStaffMembersPage />} />
              <Route path="*" element={<NavigateToNotFound />} />
            </Route>

            <Route path="*" element={<NavigateToNotFound />} />
          </Route>
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Route>

      {/* Catch-all 404 */}
      <Route path="*" element={<NotFoundPage />} />

      {/* Legacy redirects for backward compatibility */}
      <Route path="/login" element={<Navigate to="/fr/login" replace />} />
      <Route path="/dashboard" element={<Navigate to="/fr/1/medical/dashboard" replace />} />
      <Route path="/auth/select-project" element={<Navigate to="/fr/auth/select-project" replace />} />
    </Routes>
  )
}
